// Diagramas SVG (ilustrações de fluxo) para a Central de Ajuda.
// São imagens vetoriais autocontidas — não dependem de screenshots nem do
// ambiente em execução, e ficam versionadas junto com o conteúdo.

interface Passo {
  texto: string;
  cor?: string;
}

const CORES = {
  caixa: "#eef2ff",
  borda: "#c7d2fe",
  texto: "#1e293b",
  seta: "#94a3b8",
  destaque: "#fef9c3",
  sucesso: "#dcfce7",
};

function FluxoHorizontal({ passos }: { passos: Passo[] }) {
  const L = passos.length;
  const largura = 180;
  const altura = 66;
  const gap = 46;
  const total = L * largura + (L - 1) * gap;

  return (
    <svg
      viewBox={`0 0 ${total} ${altura}`}
      className="w-full h-auto"
      role="img"
      aria-label="Diagrama de fluxo"
    >
      {passos.map((p, i) => {
        const x = i * (largura + gap);
        return (
          <g key={i}>
            <rect
              x={x}
              y={0}
              width={largura}
              height={altura}
              rx={10}
              fill={p.cor || CORES.caixa}
              stroke={CORES.borda}
              strokeWidth={1.5}
            />
            <text
              x={x + largura / 2}
              y={altura / 2 + 5}
              textAnchor="middle"
              fontSize={14}
              fontWeight={600}
              fill={CORES.texto}
            >
              {p.texto}
            </text>
            {i < L - 1 && (
              <g>
                <line
                  x1={x + largura}
                  y1={altura / 2}
                  x2={x + largura + gap}
                  y2={altura / 2}
                  stroke={CORES.seta}
                  strokeWidth={2}
                />
                <polygon
                  points={`${x + largura + gap},${altura / 2} ${x + largura + gap - 8},${altura / 2 - 5} ${x + largura + gap - 8},${altura / 2 + 5}`}
                  fill={CORES.seta}
                />
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function FluxoVertical({ passos }: { passos: Passo[] }) {
  const L = passos.length;
  const largura = 260;
  const altura = 46;
  const gap = 30;
  const total = L * altura + (L - 1) * gap;

  return (
    <svg
      viewBox={`0 0 ${largura} ${total}`}
      className="w-full max-w-sm h-auto"
      role="img"
      aria-label="Diagrama de fluxo vertical"
    >
      {passos.map((p, i) => {
        const y = i * (altura + gap);
        return (
          <g key={i}>
            <rect
              x={0}
              y={y}
              width={largura}
              height={altura}
              rx={10}
              fill={p.cor || CORES.caixa}
              stroke={CORES.borda}
              strokeWidth={1.5}
            />
            <text
              x={largura / 2}
              y={y + altura / 2 + 5}
              textAnchor="middle"
              fontSize={14}
              fontWeight={600}
              fill={CORES.texto}
            >
              {p.texto}
            </text>
            {i < L - 1 && (
              <g>
                <line
                  x1={largura / 2}
                  y1={y + altura}
                  x2={largura / 2}
                  y2={y + altura + gap}
                  stroke={CORES.seta}
                  strokeWidth={2}
                />
                <polygon
                  points={`${largura / 2},${y + altura + gap} ${largura / 2 - 5},${y + altura + gap - 8} ${largura / 2 + 5},${y + altura + gap - 8}`}
                  fill={CORES.seta}
                />
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function DiagramaTicketVida() {
  return (
    <FluxoVertical
      passos={[
        { texto: "Aberto" },
        { texto: "Em Andamento" },
        { texto: "Aguardando Cliente" },
        { texto: "Resolvido" },
        { texto: "Fechado", cor: CORES.sucesso },
      ]}
    />
  );
}

function DiagramaAprovacao() {
  return (
    <FluxoHorizontal
      passos={[
        { texto: "Ticket com estimativa" },
        { texto: "Aguardando Aprovação", cor: CORES.destaque },
        { texto: "Cliente aprova" },
        { texto: "Em Andamento", cor: CORES.sucesso },
      ]}
    />
  );
}

function DiagramaPropostaFatura() {
  return (
    <FluxoHorizontal
      passos={[
        { texto: "Rascunho" },
        { texto: "Enviada" },
        { texto: "Aprovada" },
        { texto: "Fatura gerada", cor: CORES.sucesso },
      ]}
    />
  );
}

function DiagramaFaturamento() {
  return (
    <FluxoHorizontal
      passos={[
        { texto: "Apontamentos" },
        { texto: "Faturar em Lote" },
        { texto: "Fatura (rascunho)" },
        { texto: "Enviar ao cliente" },
      ]}
    />
  );
}

export const diagramas: Record<string, () => JSX.Element> = {
  "ticket-vida": DiagramaTicketVida,
  aprovacao: DiagramaAprovacao,
  "proposta-fatura": DiagramaPropostaFatura,
  faturamento: DiagramaFaturamento,
};
